import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import httpClient from '../utils/httpClient'
import { getDeviceId } from '../composables/useDeviceId'
import { useSettingsStore } from './useSettingsStore'
import { useAuthStore } from './useAuthStore'

/**
 * Chat store — ephemeral conversation state for the #32 chatbot.
 *
 * Server is stateless; the store just keeps the bubble list for the
 * current session. Bubbles reset each time the sheet opens, per the
 * design decision that every session is fresh (no server memory, and
 * no visually confusing history from a previous question).
 */

export type ChatRole = 'user' | 'assistant' | 'error'

export interface ChatMessage {
  id: number
  role: ChatRole
  text: string
  ts: number
  metadata?: Record<string, unknown>
}

export const useChatStore = defineStore('chat', () => {
  const messages = ref<ChatMessage[]>([])
  const isLoading = ref(false)
  const rateRemaining = ref<number | null>(null)
  // Explicit sheet-visible state, separate from hasMessages (#47).
  // The × button now dismisses (sets false) without wiping bubbles;
  // the new "+" button calls clear() for a fresh start. sendMessage
  // auto-opens the sheet so the user's submission is immediately
  // visible even after a previous dismiss.
  const sheetVisible = ref(false)
  // Server-side conversation id (#53). Nullable — the server creates
  // one on first send and echoes it back; subsequent turns include it
  // so the whole thread lives in a single chat_conversation row. Reset
  // on clear() (new chat = new conversation).
  const conversationId = ref<string | null>(null)
  let nextId = 1

  const hasMessages = computed(() => messages.value.length > 0)

  // Auth transitions flip the user into a different rate-limit bucket
  // (anon 5/day vs authed 50/day). Reset the cached hint and strip any
  // stale rate-limit error bubble so the CTA + "0 left" hint disappear
  // the moment the user signs in. Same logic on logout — the new anon
  // bucket is a clean slate from the client's perspective.
  const auth = useAuthStore()
  watch(
    () => auth.isLoggedIn,
    (now, prev) => {
      if (now === prev) return
      rateRemaining.value = null
      messages.value = messages.value.filter(
        (m) => m.metadata?.kind !== 'rate_limit_anon',
      )
    },
  )

  function _push(role: ChatRole, text: string, metadata?: Record<string, unknown>) {
    messages.value.push({
      id: nextId++,
      role,
      text,
      ts: Date.now(),
      metadata,
    })
  }

  /** Reset everything — wipe history, drop rate state, collapse the
   *  sheet. Fires from the "+" / new-chat button (and from the close-
   *  chat voice/command flow via the pending-close timer). */
  function clear(): void {
    messages.value = []
    rateRemaining.value = null
    sheetVisible.value = false
    conversationId.value = null
    cancelPendingClose()
  }

  /** Hide the history sheet without dropping bubbles. The next
   *  sendMessage re-opens the sheet so the user sees their prior
   *  conversation plus the new turn. Fires from the × button. */
  function dismiss(): void {
    sheetVisible.value = false
  }

  /** Pending close-chat timer started by _applyAction. Stored so a
   *  subsequent sendMessage or manual clear can cancel it — otherwise
   *  a "bye" then a quick follow-up would yank the new bubbles out. */
  let closeTimer: ReturnType<typeof setTimeout> | null = null
  function cancelPendingClose() {
    if (closeTimer !== null) {
      clearTimeout(closeTimer)
      closeTimer = null
    }
  }

  /** How many prior turns to echo to the server for context. 3 pairs
   *  (user + assistant) keeps the prompt budget modest while letting
   *  follow-ups like "how about the day after tomorrow?" inherit the
   *  previous location + activity. */
  const CONTEXT_TURNS = 3

  function buildContext() {
    // Error bubbles shouldn't be sent back as 'assistant' turns — they
    // were never generated by the model. Content-blocked turns are
    // also excluded (both the user's offending message AND the polite
    // refusal) — including them would cascade the block into the next
    // otherwise-benign message, since Gemini evaluates the whole
    // conversation history.
    const usable = messages.value.filter(
      (m) => (m.role === 'user' || m.role === 'assistant')
        && !m.metadata?.excludeFromContext,
    )
    return usable.slice(-CONTEXT_TURNS * 2).map((m) => ({
      role: m.role,
      text: m.text,
    }))
  }

  function currentLocationString(): string | undefined {
    // Use the user's currently-selected forecast location as the default
    // target for queries that don't name one ("Will it rain tomorrow?").
    // Shape 'City, CC' matches what the parse prompt expects.
    const settings = useSettingsStore()
    const city = (settings.location || '').trim()
    if (!city) return undefined
    const cc = (settings.countryCode || '').trim()
    return cc ? `${city}, ${cc}` : city
  }

  function currentModelString(): string | undefined {
    // Mirror whichever model(s) drive the user's forecast card. The
    // backend takes the first comma-separated value for /swim/daily so
    // the chat reply matches what the user sees on screen.
    const settings = useSettingsStore()
    const model = (settings.modelString || '').trim()
    return model || undefined
  }

  /** @param opts.locationOverride  When set, sent to the API as
   *  `current_location`, bypassing the settings-derived default. Used
   *  by the alt-location chip to re-ask the same question pinned to a
   *  specific alternative ("Bristol, TN, US" instead of the UK Bristol). */
  async function sendMessage(
    text: string,
    opts?: { locationOverride?: string },
  ): Promise<void> {
    const trimmed = text.trim()
    if (!trimmed || isLoading.value) return
    // A fresh message aborts any pending close-chat — user changed
    // their mind or is continuing the conversation.
    cancelPendingClose()
    const ctx = buildContext()
    const currentLocation = opts?.locationOverride ?? currentLocationString()
    const model = currentModelString()
    const settings = useSettingsStore()
    const language = (settings.language || '').trim() || undefined
    // Browser-resolved IANA timezone ('Asia/Hong_Kong', 'Europe/London', …).
    // Lets the server compute the user's *local* today rather than UTC
    // today — matters for relative terms near local midnight.
    let timezone: string | undefined
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
    } catch { timezone = undefined }
    _push('user', trimmed)
    // Any send implicitly opens the sheet — covers first-message and
    // the re-open-after-dismiss case alike.
    sheetVisible.value = true
    isLoading.value = true
    try {
      // X-Chat-Storage-Opt-Out lets the server honour an anon user's
      // Settings toggle (localStorage-only for anon — nowhere else to
      // read it). Signed-in users' preference lives on wital_user so
      // the header is redundant but harmless; the server ignores it
      // for authed requests.
      const optOutHeader = settings.chatStorageOptedOut ? 'true' : 'false'
      const resp = await httpClient.post(
        '/api/chat/weather',
        {
          text: trimmed,
          context: ctx,
          current_location: currentLocation,
          model,
          language,
          timezone,
          conversation_id: conversationId.value,
        },
        {
          headers: {
            'X-Device-Id': getDeviceId(),
            'X-Chat-Storage-Opt-Out': optOutHeader,
          },
        },
      )
      // httpClient sets validateStatus: status < 500, so 4xx resolves
      // here rather than throwing. Route non-2xx explicitly so rate-
      // limit / parse errors surface as error bubbles instead of an
      // empty assistant reply.
      const status = resp.status ?? 200
      if (status >= 400) {
        const detail = (resp.data && typeof resp.data === 'object') ? resp.data.detail : undefined
        _pushStatusError(status, typeof detail === 'string' ? detail : undefined)
        return
      }
      const data = resp.data || {}
      const metadata = (data.metadata || {}) as Record<string, unknown>
      if (typeof metadata.rate_remaining === 'number') {
        rateRemaining.value = metadata.rate_remaining as number
      }
      // Server hands back the conversation id it created / reused.
      // Stash it so the next send threads onto the same row.
      if (typeof metadata.conversation_id === 'string' && metadata.conversation_id) {
        conversationId.value = metadata.conversation_id as string
      }
      // Safety-filter refusals: mark this bubble + the preceding user
      // turn as excluded from future context so the next benign send
      // doesn't carry the blocked content forward to Gemini (which
      // would cascade the block). User still sees both bubbles.
      const blocked = metadata.content_blocked === true
      const assistantMeta = blocked
        ? { ...metadata, excludeFromContext: true }
        : metadata
      if (blocked) {
        for (let i = messages.value.length - 1; i >= 0; i--) {
          const m = messages.value[i]
          if (m.role === 'user') {
            m.metadata = { ...(m.metadata || {}), excludeFromContext: true }
            break
          }
        }
      }
      _push('assistant', (data.reply || '').toString().trim() || '(no reply)', assistantMeta)
      // Apply any structured action returned by a settings_command — the
      // prose reply shown above is the user-facing confirmation; here
      // we actually mutate the settings store so the forecast card /
      // i18n reload.
      await _applyAction(metadata.action)
    } catch (err: any) {
      const status = err?.response?.status
      const detail = err?.response?.data?.detail
      _pushStatusError(status, typeof detail === 'string' ? detail : undefined)
    } finally {
      isLoading.value = false
    }
  }

  /** Apply a server-issued action to client state. Settings commands
   *  arrive as `metadata.action: { kind, … }`; we mutate the settings
   *  store so the forecast card / i18n pick up the change without the
   *  user navigating to the Settings drawer. Unknown or malformed
   *  actions are ignored — backend stays the authority on what can be
   *  applied; a future unknown kind just falls through silently. */
  async function _applyAction(action: unknown): Promise<void> {
    if (!action || typeof action !== 'object') return
    const a = action as Record<string, unknown>
    const kind = a.kind
    try {
      if (kind === 'set_location') {
        const name = typeof a.location_name === 'string' ? a.location_name : ''
        const cc = typeof a.country_code === 'string' ? a.country_code : ''
        if (!name) return
        const settings = useSettingsStore()
        settings.setLocation(name, cc)
      } else if (kind === 'set_language') {
        const code = typeof a.code === 'string' ? a.code : ''
        if (!code) return
        const settings = useSettingsStore()
        await settings.setLanguage(code)
      } else if (kind === 'close_chat') {
        // Let the farewell bubble breathe before we collapse the
        // panel. We dismiss (hide the sheet) rather than clear (wipe
        // messages) — the intent classifier sometimes mis-tags an
        // ordinary weather question as 'goodbye' (trigger words like
        // 'stop', 'done', 'see' all show up in normal queries), and a
        // misclassification that *destroys* the user's conversation
        // is the user-reported "the whole chatbot box disappears".
        // With dismiss(), a misclassification is recoverable: the
        // user's next sendMessage re-opens the sheet with the full
        // prior thread intact. Any new sendMessage cancels this timer
        // outright. */
        cancelPendingClose()
        closeTimer = setTimeout(() => {
          closeTimer = null
          dismiss()
        }, 1500)
      }
    } catch (e) {
      console.warn('chat: failed to apply action', kind, e)
    }
  }

  /** Classify a non-2xx status into an error bubble. For 429 we also
   *  stash a { kind, action } on metadata so ChatBar can render an
   *  auth-aware translated message + sign-in CTA (the action is only
   *  emitted for anonymous callers — signed-in users have already
   *  spent their 50/day and a CTA would be pointless). */
  function _pushStatusError(status: number | undefined, serverDetail?: string) {
    if (status === 429) {
      rateRemaining.value = 0
      const auth = useAuthStore()
      if (auth.isLoggedIn) {
        _push('error', '', { kind: 'rate_limit_auth' })
      } else {
        _push('error', '', { kind: 'rate_limit_anon', action: 'signIn' })
      }
      return
    }
    let msg: string
    if (status === 400) {
      msg = serverDetail || 'Could not parse your message. Try including a city name.'
    } else {
      msg = serverDetail || 'Something went wrong. Please try again in a moment.'
    }
    _push('error', msg)
  }

  return {
    messages,
    isLoading,
    rateRemaining,
    hasMessages,
    sheetVisible,
    clear,
    dismiss,
    sendMessage,
  }
})
